<template>
  <Head title="Login" />
  <div class="flex w-full flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none xl:w-1/3">
    <div class="mx-auto w-full max-w-sm">
      <div v-if="!props.twoFactorChallenge">
        <SpectreHeading
          as="h2"
          size="2xl"
          class="mt-8"
        >
          Sign into Spectre
        </SpectreHeading>

        <SpectreText
          v-if="props.status"
          as="p"
          size="sm"
          color="secondary"
          class="mt-4"
        >
          {{ props.status }}
        </SpectreText>

        <Transition
          name="fade"
          mode="out-in"
          @after-enter="focusStep"
        >
          <!-- Step 1: identify (email first) -->
          <form
            v-if="!emailConfirmed"
            key="email"
            data-testid="email-step"
            class="mt-10 space-y-6"
            @submit.prevent="continueToMethod"
          >
            <SpectreField
              class="space-y-2"
              required
              :invalid="!!emailError"
            >
              <SpectreLabel>
                Email address
              </SpectreLabel>
              <SpectreInput
                v-model="form.email"
                type="email"
                placeholder="e.g. email@example.com"
                autofocus
              />
              <SpectreErrorMessage>
                {{ emailError }}
              </SpectreErrorMessage>
            </SpectreField>
            <SpectreButton
              block
              type="submit"
              data-testid="continue-button"
              :disabled="!form.email.trim()"
            >
              Continue
            </SpectreButton>
          </form>

          <!-- Step 2a: internal staff — Google only -->
          <div
            v-else-if="forceGoogleLogin"
            key="google"
            data-testid="google-step"
            class="mt-10 space-y-6"
          >
            <div class="space-y-1">
              <SpectreText
                as="p"
                size="sm"
                color="secondary"
              >
                Street Group accounts must sign in with Google
              </SpectreText>
              <SpectreText
                as="p"
                size="sm"
                weight="medium"
              >
                {{ form.email }}
              </SpectreText>
            </div>
            <SpectreField :invalid="!!emailError">
              <SpectreErrorMessage>
                {{ emailError }}
              </SpectreErrorMessage>
            </SpectreField>
            <SpectreButton
              block
              data-testid="google-login-button"
              :loading="googleRedirecting"
              @click="goToGoogleLogin"
            >
              Sign in with Google
            </SpectreButton>
            <div class="text-sm leading-6">
              <SpectreTextLink
                data-testid="use-different-email"
                as="button"
                type="button"
                @click="useADifferentEmail"
              >
                Use a different email
              </SpectreTextLink>
            </div>
          </div>

          <!-- Step 2b: everyone else — password (+ SSO) -->
          <div
            v-else
            key="password"
            data-testid="password-step"
          >
            <form
              class="mt-10 space-y-6"
              @submit.prevent="submit"
            >
              <!-- Pair a username field with the password so password managers autofill and submit correctly. -->
              <input
                :value="form.email"
                type="email"
                name="email"
                autocomplete="username"
                class="sr-only"
                tabindex="-1"
                aria-hidden="true"
                readonly
              >
              <SpectreField
                class="space-y-2"
                required
                :invalid="!!emailError"
              >
                <SpectreLabel>
                  Email address
                </SpectreLabel>
                <SpectreText
                  as="p"
                  size="sm"
                  class="w-full py-1.5"
                >
                  {{ form.email }}
                </SpectreText>
                <SpectreTextLink
                  v-if="safeOriginUrl"
                  :href="safeOriginUrl"
                >
                  Use another account
                </SpectreTextLink>
                <SpectreTextLink
                  v-else
                  data-testid="use-different-email"
                  as="button"
                  type="button"
                  @click="useADifferentEmail"
                >
                  Use a different email
                </SpectreTextLink>
                <SpectreErrorMessage>
                  {{ emailError }}
                </SpectreErrorMessage>
              </SpectreField>
              <SpectreField
                class="space-y-2"
                required
                :invalid="!!form.errors.password"
              >
                <SpectreLabel>
                  Password
                </SpectreLabel>
                <SpectrePasswordInput
                  v-model="form.password"
                  autocomplete="current-password"
                />
                <SpectreErrorMessage>
                  {{ form.errors.password }}
                </SpectreErrorMessage>
              </SpectreField>
              <div class="flex items-center justify-between">
                <SpectreCheckboxField
                  :checked="form.remember"
                  @update:checked="(value: CheckboxCheckedState) => form.remember = !!value"
                >
                  <SpectreCheckbox />
                  <SpectreLabel>
                    Remember me
                  </SpectreLabel>
                </SpectreCheckboxField>

                <div class="text-sm leading-6">
                  <SpectreTextLink
                    v-if="canResetPassword"
                    :href="route('password.request')"
                  >
                    Forgot password?
                  </SpectreTextLink>
                </div>
              </div>
              <SpectreButton
                block
                :loading="processing"
              >
                Sign in
              </SpectreButton>
            </form>
            <div
              v-if="ssoFeatureEnabled"
              class="mt-4"
            >
              <div>
                <SpectreDivider>
                  or
                </SpectreDivider>
                <div class="mt-4">
                  <SpectreButton
                    block
                    type="button"
                    color="secondary"
                    data-testid="sso-login-button"
                    @click="() => router.visit(route('login.sso'))"
                  >
                    Sign in with SSO
                  </SpectreButton>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>

      <div
        v-else
        class="mt-8"
      >
        <TwoFactorChallenge />
      </div>
      <div
        v-if="!props.twoFactorChallenge"
        class="mt-4"
      >
        <Policy class="mt-4" />
      </div>
    </div>
  </div>
  <AuthBanner />
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue';
import Policy from '@/Components/Auth/Policy.vue';
import {
  SpectreInput,
  SpectrePasswordInput,
  SpectreButton,
  SpectreCheckbox,
  SpectreDivider,
  SpectreField,
  SpectreLabel,
  SpectreErrorMessage,
  SpectreHeading,
  SpectreText,
  SpectreTextLink,
  SpectreCheckboxField,
} from '@agentsoftware/spectre-ui';
import AuthPageContainerLayout from '@/Layouts/Auth/AuthPageContainerLayout.vue';
import useAuth from '@/Composables/useAuth';
import {
  Head,
  router,
  usePage,
} from '@inertiajs/vue3';
import AuthBanner from '@/Components/Auth/AuthBanner.vue';
import TwoFactorChallenge from '@/Components/Auth/TwoFactorChallenge.vue';
import type { CheckboxCheckedState } from '@ark-ui/vue/checkbox';

const { signInForm: form } = useAuth();
const processing = ref(false);
const googleRedirecting = ref(false);

const props = defineProps<{
  canResetPassword?: boolean;
  status?: string;
  email?: string;
  originUrl?: string;
  twoFactorChallenge?: boolean;
  internalEmailDomains?: string[];
  internalGoogleEnforced?: boolean;
}>();

defineOptions({
  layout: AuthPageContainerLayout,
});

// Identifier-first: a pre-filled email (e.g. arriving from another product) skips straight to the method step.
const emailConfirmed = ref<boolean>(!!props.email);

onBeforeMount(() => {
  form.email = props.email || '';
});

// Defence-in-depth: never bind a non-http(s) URL (e.g. javascript:) to the href sink.
const safeOriginUrl = computed((): string | undefined => {
  if (!props.originUrl) {
    return undefined;
  }
  try {
    const { protocol } = new URL(props.originUrl);
    return protocol === 'http:' || protocol === 'https:' ? props.originUrl : undefined;
  } catch {
    return undefined;
  }
});

const ssoFeatureEnabled = computed((): boolean => usePage().props.ssoFeatureEnabled as boolean);

// Surface both live form errors and flashed session errors (e.g. a rejected Google callback redirects back with one).
const emailError = computed((): string | undefined => form.errors.email || usePage().props.errors?.email);

const forceGoogleLogin = computed((): boolean => {
  if (!props.internalGoogleEnforced) {
    return false;
  }

  const domain = (form.email.trim().split('@')[1] || '').toLowerCase();

  return (props.internalEmailDomains || []).includes(domain);
});

const continueToMethod = (): void => {
  if (!form.email.trim()) {
    return;
  }

  emailConfirmed.value = true;
};

const goToGoogleLogin = (): void => {
  googleRedirecting.value = true;
  window.location.href = route('staff.google.redirect');
};

const useADifferentEmail = (): void => {
  emailConfirmed.value = false;
  form.email = '';
  form.clearErrors();
};

// Move keyboard/screen-reader focus to the first control of the step that just appeared.
const focusStep = (element: Element): void => {
  element.querySelector<HTMLElement>('input, button, a, [tabindex]')?.focus();
};

const submit = () => {
  form.post(route('login'), {
    onStart: () => {
      processing.value = true;
    },
    onError: (errors) => {
      processing.value = false;
      form.reset('password');

      if (errors.two_factor_required) {
        router.visit(route('login'));
      }
    },
    onFinish: () => {
      processing.value = false;
    },
  });
};

</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 200ms cubic-bezier(0.22, 1, 0.36, 1), transform 200ms cubic-bezier(0.22, 1, 0.36, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

@media (prefers-reduced-motion: reduce) {
  .fade-enter-active,
  .fade-leave-active {
    transition-duration: 1ms;
  }

  .fade-enter-from,
  .fade-leave-to {
    transform: none;
  }
}
</style>
